Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "AtlasFetcher", {
    enumerable: true,
    get: function() {
        return AtlasFetcher;
    }
});
const _errors = require("./errors.cjs");
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
/**
 * Type guard to check if an object conforms to the AtlasDef interface.
 * @param obj - The object to check
 * @returns True if the object is a valid AtlasDef, false otherwise
 */ function isAtlasDef(obj) {
    return !!obj && typeof obj === 'object' && 'version' in obj;
}
class AtlasFetcher {
    /**
     * Fetches Atlas definitions from the configured URL.
     * Prevents concurrent fetches by returning undefined if a fetch is already in progress.
     * Only returns new definitions if the version has changed since the last fetch.
     * @returns Promise that resolves to AtlasDef if new version found, undefined otherwise
     * @throws {AtlasFetchError} When the HTTP request fails
     * @throws {AtlasParseError} When the response cannot be parsed as valid JSON or AtlasDef
     */ async fetch() {
        if (this.isFetching) {
            // eslint-disable-next-line no-undefined
            return Promise.resolve(undefined);
        }
        this.isFetching = true;
        this.initializeTimestamp();
        try {
            const response = await this.performFetch();
            return await this.performParse(response);
        } finally{
            this.isFetching = false;
        }
    }
    /**
     * Attempts to refresh Atlas definitions if enough time has passed since the last refresh.
     * This method is designed for periodic updates and will not throw errors.
     * Instead, it logs them and allows the next refresh attempt to proceed.
     * @returns Promise that resolves to AtlasDef if new version found, undefined otherwise
     */ async refresh() {
        if (!this.enabled) return; // No auto-refresh if interval is 0
        const now = Date.now();
        if (now - this.lastTimeMs < this.intervalMs) {
            return; // Not enough time has passed
        }
        this.lastTimeMs = now;
        try {
            return await this.fetch();
        } catch (err) {
            // Log the error but do not throw it, as this is a refresh operation.
            // It will be attempted again after the interval.
            this.logger.error(err instanceof Error ? err.message : String(err));
        }
    }
    /**
     * Initializes the timestamp for the first fetch operation.
     * Sets lastTimeMs to the current time if it hasn't been set yet.
     * @private
     */ initializeTimestamp() {
        if (this.lastTimeMs === 0) {
            this.lastTimeMs = Date.now();
        }
    }
    /**
     * Performs the HTTP request to fetch Atlas definitions.
     * @private
     * @returns Promise that resolves to the HTTP Response
     * @throws {AtlasFetchError} When the request fails or returns a non-OK status
     */ async performFetch() {
        try {
            const response = await fetch(this.url);
            if (!response?.ok) {
                throw new _errors.AtlasFetchError(`Failed to fetch definitions (${response?.status})`);
            }
            return response;
        } catch (err) {
            if (err instanceof _errors.AtlasFetchError) {
                throw err;
            }
            throw new _errors.AtlasFetchError(`Failed to fetch definitions: ${err instanceof Error ? err.message : String(err)}`);
        }
    }
    /**
     * Parses the HTTP response and validates it as a valid AtlasDef.
     * @private
     * @param response - The HTTP response to parse
     * @returns Promise that resolves to AtlasDef if new version, undefined if same version
     * @throws {AtlasParseError} When JSON parsing fails or structure is invalid
     */ async performParse(response) {
        try {
            const json = await response.json();
            if (!isAtlasDef(json)) {
                throw new Error('Invalid JSON structure');
            }
            return this.performVersionCheck(json);
        } catch (err) {
            throw new _errors.AtlasParseError(`Failed to parse definitions JSON: ${err instanceof Error ? err.message : String(err)}`);
        }
    }
    /**
     * Checks if the fetched Atlas definition has a different version than the last one.
     * Updates the internal version tracking and returns the definition only if it's new.
     * @private
     * @param json - The parsed AtlasDef object to check
     * @returns The AtlasDef if version is new, undefined if version is the same
     */ performVersionCheck(json) {
        if (this.lastVersion !== json.version) {
            this.lastVersion = json.version;
            return json;
        }
    }
    /**
     * Creates a new AtlasFetcher instance.
     * @param url - The URL to fetch Atlas definitions from
     * @param intervalMs - The refresh interval in milliseconds (0 disables auto-refresh)
     * @param logger - Logger instance for error reporting
     */ constructor(url, intervalMs, logger){
        _define_property(this, "url", void 0);
        _define_property(this, "intervalMs", void 0);
        _define_property(this, "lastTimeMs", 0);
        _define_property(this, "lastVersion", '');
        _define_property(this, "logger", void 0);
        _define_property(this, "isFetching", false);
        _define_property(this, "enabled", void 0);
        this.url = url;
        this.intervalMs = intervalMs;
        this.logger = logger;
        this.enabled = intervalMs > 0; // Enable if interval is positive
    }
}
